import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "countryButton",
    "countryCount",
    "countryOption",
    "countryRow",
    "filterBar",
    "freshButton",
    "item",
    "seenButton",
    "typeButton"
  ]

  connect() {
    this.loadStateFromUrl()
    this.boundOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.boundOutsideClick)
    this.update()
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick)
  }

  selectType(event) {
    event.preventDefault()
    const nextType = event.currentTarget.dataset.filterType
    if (this.activeType === nextType) return

    this.activeType = nextType
    this.trackFilterEvent("type", nextType)
    this.update()
  }

  toggleCountryPanel(event) {
    if (!this.hasCountryRowTarget || !this.hasCountryButtonTarget) {
      return
    }

    event.preventDefault()
    this.setCountryPanelOpen(this.countryRowTarget.dataset.open !== "true")
  }

  toggleCountry(event) {
    if (!this.hasCountryOptionTarget) {
      return
    }

    event.preventDefault()

    const code = event.currentTarget.dataset.countryCode
    let selected

    if (this.selectedCountries.has(code)) {
      this.selectedCountries.delete(code)
      selected = false
    } else {
      this.selectedCountries.add(code)
      selected = true
    }

    this.trackFilterEvent("country", code, { selected })
    this.update()
  }

  toggleFresh(event) {
    if (!this.hasFreshButtonTarget) {
      return
    }

    event.preventDefault()

    this.freshOnly = !this.freshOnly
    this.trackFilterEvent("fresh", this.freshOnly ? "active" : "inactive")
    this.update()
  }

  selectSeen(event) {
    if (!this.hasSeenButtonTarget) {
      return
    }

    event.preventDefault()

    const nextFilter = event.currentTarget.dataset.filterSeen
    this.seenFilter = this.seenFilter === nextFilter ? null : nextFilter
    this.trackFilterEvent("seen", this.seenFilter || "all")
    this.update()
  }

  handleOutsideClick(event) {
    if (!this.hasCountryButtonTarget || !this.hasCountryRowTarget || !this.hasFilterBarTarget) {
      return
    }

    if (this.filterBarTarget.contains(event.target)) return
    if (this.countryRowTarget.contains(event.target)) return

    this.setCountryPanelOpen(false)
  }

  update() {
    this.typeButtonTargets.forEach((button) => {
      button.dataset.active = button.dataset.filterType === this.activeType ? "true" : "false"
    })

    this.countryOptionTargets.forEach((option) => {
      const active = this.selectedCountries.has(option.dataset.countryCode)
      option.dataset.active = active ? "true" : "false"
      option.setAttribute("aria-pressed", active ? "true" : "false")
    })

    const selectedCount = this.selectedCountries.size
    if (this.hasCountryButtonTarget) {
      this.countryButtonTarget.dataset.active = selectedCount > 0 ? "true" : "false"
    }
    if (this.hasCountryCountTarget) {
      this.countryCountTarget.hidden = selectedCount === 0
      this.countryCountTarget.textContent = String(selectedCount)
    }
    if (this.hasFreshButtonTarget) {
      this.freshButtonTarget.dataset.active = this.freshOnly ? "true" : "false"
    }
    if (this.hasSeenButtonTarget) {
      this.seenButtonTargets.forEach((button) => {
        button.dataset.active = button.dataset.filterSeen === this.seenFilter ? "true" : "false"
      })
    }

    this.itemTargets.forEach((item) => {
      const tags = item.dataset.filterTags ? item.dataset.filterTags.split("|").filter(Boolean) : []
      const country = item.dataset.filterCountry
      const badge = item.dataset.filterBadge
      const seen = item.dataset.filterSeen
      const matchesType = this.activeType === "all" || tags.includes(this.activeType)
      const matchesCountry = selectedCount === 0 || this.selectedCountries.has(country)
      const matchesFresh = !this.freshOnly || badge === "new" || badge === "updated"
      const matchesSeen = !this.seenFilter || seen === this.seenFilter
      const visible = matchesType && matchesCountry && matchesFresh && matchesSeen

      item.hidden = !visible
      item.style.display = visible ? "" : "none"
    })

    this.syncUrl()
  }

  setCountryPanelOpen(open) {
    if (!this.hasCountryRowTarget || !this.hasCountryButtonTarget) {
      return
    }

    this.countryRowTarget.dataset.open = open ? "true" : "false"
    this.countryRowTarget.setAttribute("aria-hidden", open ? "false" : "true")
    this.countryButtonTarget.setAttribute("aria-expanded", open ? "true" : "false")
  }

  loadStateFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const requestedType = params.get("type")
    const availableTypes = new Set(this.typeButtonTargets.map((button) => button.dataset.filterType))

    this.activeType = availableTypes.has(requestedType) ? requestedType : "all"
    this.selectedCountries = new Set(
      (params.get("countries") || "")
        .split(",")
        .map((value) => value.trim().toUpperCase())
        .filter(Boolean)
    )
    this.freshOnly = params.get("fresh") === "1"
    const requestedSeen = params.get("seen")
    this.seenFilter = this.hasSeenButtonTarget && ["seen", "unseen"].includes(requestedSeen) ? requestedSeen : null
  }

  syncUrl() {
    const url = new URL(window.location.href)

    if (this.activeType !== "all") {
      url.searchParams.set("type", this.activeType)
    } else {
      url.searchParams.delete("type")
    }

    const countries = Array.from(this.selectedCountries).sort()
    if (countries.length > 0) {
      url.searchParams.set("countries", countries.join(","))
    } else {
      url.searchParams.delete("countries")
    }

    if (this.freshOnly) {
      url.searchParams.set("fresh", "1")
    } else {
      url.searchParams.delete("fresh")
    }

    if (this.seenFilter) {
      url.searchParams.set("seen", this.seenFilter)
    } else {
      url.searchParams.delete("seen")
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(window.history.state, "", nextUrl)
  }

  trackFilterEvent(filterKind, filterValue, extraParams = {}) {
    if (typeof window.gtag !== "function") return
    if (filterKind === "type" && !["photo", "video"].includes(filterValue)) return

    window.gtag("event", "homepage_filter_used", {
      filter_kind: filterKind,
      filter_value: filterValue,
      ...extraParams
    })
  }
}
